"use client";

import { useAuth } from "@/contexts/AuthContext";
import { db } from "@/lib/firebase";
import { useQuery } from "@tanstack/react-query";
import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";

/**
 * Check if the current user has a team in the specified tournament
 * Returns team info for UI personalization (navbar visibility)
 *
 * Note: This is for UI only. Authorization is handled server-side.
 *
 * @param tournamentId - The ID of the tournament to check
 * @returns Query result with team { id, name } or null
 */
export function useUserTeamForTournament(tournamentId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["userTeam", tournamentId, user?.id],
    queryFn: async () => {
      if (!user?.id || !tournamentId) return null;

      // Single Firestore read with limit(1) - cost: 1 read
      const teamsQuery = query(
        collection(db, "teams"),
        where("tournamentId", "==", tournamentId),
        where("playerIds", "array-contains", user.id),
        where("isActive", "==", true),
        limit(1),
      );

      const snapshot = await getDocs(teamsQuery);
      if (snapshot.empty) return null;

      const doc = snapshot.docs[0];
      return { id: doc.id, name: doc.get("name") as string };
    },
    enabled: !!user?.id && !!tournamentId, // Only run when user is authenticated and tournamentId is provided
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes - teams don't change frequently
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 1, // Only retry once on failure
  });
}
